package fr.frux.shared.domain.model

import fr.frux.shared.content.TreeCorrectionLimits

/**
 * Correction d'un champ d'identité d'un arbre (spec 2026-10-19). Une correction porte sur un seul
 * champ, [reason] est le motif facultatif joint à la révision.
 */
sealed interface TreeCorrection {
    val reason: String?

    data class Species(
        val speciesId: Long,
        val certainty: Certainty,
        override val reason: String? = null,
    ) : TreeCorrection

    data class Position(
        val latitude: Double,
        val longitude: Double,
        override val reason: String? = null,
    ) : TreeCorrection

    /** Une chaîne vide efface la note. */
    data class Note(
        val description: String,
        override val reason: String? = null,
    ) : TreeCorrection
}

/** Valeurs d'identité d'un arbre avant correction, de quoi calculer l'inverse. */
data class TreeIdentity(
    val speciesId: Long,
    val certainty: Certainty,
    val latitude: Double,
    val longitude: Double,
    val description: String?,
)

fun Tree.identity(): TreeIdentity =
    TreeIdentity(
        speciesId = species.id,
        certainty = certainty,
        latitude = latitude,
        longitude = longitude,
        description = description,
    )

/** Correction qui ramène les champs touchés à leurs valeurs d'avant, sans motif. */
fun TreeCorrection.undoFrom(before: TreeIdentity): TreeCorrection =
    when (this) {
        is TreeCorrection.Species -> TreeCorrection.Species(before.speciesId, before.certainty)
        is TreeCorrection.Position -> TreeCorrection.Position(before.latitude, before.longitude)
        is TreeCorrection.Note -> TreeCorrection.Note(before.description.orEmpty())
    }

/** Faux quand la correction ne changerait rien : le serveur n'écrirait aucune révision. */
fun TreeCorrection.changes(before: TreeIdentity): Boolean =
    when (this) {
        is TreeCorrection.Species -> {
            speciesId != before.speciesId || certainty != before.certainty
        }

        is TreeCorrection.Position -> {
            TreeCorrectionLimits.distanceMeters(before.latitude, before.longitude, latitude, longitude) >=
                TreeCorrectionLimits.POSITION_NOISE_METERS
        }

        is TreeCorrection.Note -> {
            description.trim() != before.description.orEmpty().trim()
        }
    }

/** Pied du bloc Identité : qui a corrigé en dernier, et quand. [authorName] nul pour une correction d'opérateur. */
data class TreeCorrectionMark(
    val authorName: String?,
    val atMillis: Long,
)
